import {
  MeetingJoinInfo,
  MeetingStatus,
  User,
  UserType,
  ProposedTimeSlot,
  Timeslot,
  CalendarSlot,
} from "./types"

/**
 * Represents the data required to check a meeting's status.
 */
interface MeetingScheduleData {
  meetingType: MeetingStatus,
  created?: Date | undefined,
  startTime: Date,
  endTime: Date,
  realEndTime?: Date,
  realStartTime?: Date,
  joinedInfo?: MeetingJoinInfo[],
}

/**
 * Retrieves the question details based on the provided question ID.
 * @param questionId The ID of the question to retrieve.
 * @returns An object containing the question text, whether text response is allowed, and validity.
 */
export const getQuestion = (questionId: string): {
  questionText: string;
  allowTextResponse: boolean;
  valid: boolean;
} => {
  if (questionId === "0001") {
    return {
      valid: true,
      questionText: "This telehealth session provided me with the capabilities to accomplish what I need to do.",
      allowTextResponse: false
    }
  }

  if (questionId === "0002") {
    return {
      valid: true,
      questionText: "The telehealth system was easy to use for this session.",
      allowTextResponse: false
    }
  }

  return {
    valid: false,
    questionText: "",
    allowTextResponse: false
  }
}

/**
 * Checks if a proposed meeting has expired based on its creation date.
 * @param meeting The meeting schedule data to check.
 * @returns A boolean indicating whether the proposed meeting has expired.
 */
const proposedMeetingExpired = (
  meeting: MeetingScheduleData
): boolean => {
  if (!meeting.created) {
    return false
  }

  const lastValidCreateTime = new Date()
  lastValidCreateTime.setDate(lastValidCreateTime.getDate() - 14)

  return lastValidCreateTime.getTime() - meeting.created.getTime() >= 0
}

/**
 * Determines the current status of a meeting based on its schedule data.
 * @param meeting The meeting schedule data to evaluate.
 * @param disableJoinOffset Optional flag to disable the join offset.
 * @returns An object containing the meeting status, a message, and whether the user can join.
 */
export const getMeetingStatus = (
  meeting: MeetingScheduleData,
  disableJoinOffset?: boolean,
): {
  status: MeetingStatus,
  message: string,
  canJoin: boolean,
} => {

  if (!meeting) {
    return {
      status: MeetingStatus.Abandoned,
      message: 'Cannot join',
      canJoin: false,
    }
  }

  if (meeting.meetingType === MeetingStatus.Cancelled) {
    return {
      status: MeetingStatus.Cancelled,
      message: 'The meeting was cancelled',
      canJoin: false,
    }
  }

  if (meeting.meetingType === MeetingStatus.Abandoned) {
    return {
      status: MeetingStatus.Abandoned,
      message: 'The meeting was abandoned',
      canJoin: false,
    }
  }

  if (meeting.meetingType === MeetingStatus.Proposed) {
    const expired = proposedMeetingExpired(meeting)
    if (expired) {
      return {
        status: MeetingStatus.Abandoned,
        message: 'The proposed meeting has expired',
        canJoin: false,
      }
    }

    return {
      status: MeetingStatus.Proposed,
      message: 'The meeting time needs to be finalised',
      canJoin: false,
    }
  }

  const now = new Date()
  // can join 15 minutes before event starts
  const smallOffset = 900000

  if (now < meeting.startTime) {
    const finalOffset = disableJoinOffset && !meeting.realStartTime ? 0 : smallOffset;
    if (now.getTime() + finalOffset <= meeting.startTime.getTime()) {
      return {
        status: MeetingStatus.Scheduled,
        message: 'The meeting has not started yet',
        canJoin: false,
      }
    }
  }

  const joinOffset = disableJoinOffset ? 0 : smallOffset;

  const joined = meeting?.joinedInfo?.filter(
    item => item.joinTime != undefined
  ) || []

  if (now > meeting.endTime) {
    // Use shorter join offset if no one has joined
    if (!meeting.realStartTime && joined.length == 0) {
      if (now.getTime() - joinOffset > meeting.endTime.getTime()) {
        return {
          status: MeetingStatus.Abandoned,
          message: 'The meeting has ended',
          canJoin: false,
        }
      }
    }

    // can join four hours after event ends
    const endOffset = disableJoinOffset ? 0 : 14400000;
    const meetingClosed = now.getTime() - endOffset >= meeting.endTime.getTime()
    // Only keep the meeting open for 15 minutes after all users leave
    const attendeesLeft = meeting.realEndTime && now.getTime() - joinOffset >= meeting.realEndTime.getTime()

    if (meetingClosed || attendeesLeft) {
      return {
        status: MeetingStatus.Ended,
        message: 'The meeting has ended',
        canJoin: false,
      }
    }

    return {
      status: MeetingStatus.Started,
      message: 'The scheduled meeting time is over',
      canJoin: true,
    }
  }

  if (meeting.realStartTime) {
    const currentTime = new Date()
    const timeSince = currentTime.getTime() - new Date(meeting.realStartTime).getTime()
    const minutesSince = Math.floor(timeSince / 60000)

    return {
      status: MeetingStatus.Started,
      message: `The meeting started ${minutesSince} minutes ago`,
      canJoin: true,
    }
  }

  return {
    status: MeetingStatus.Started,
    message: 'Can join the meeting',
    canJoin: true,
  }
}

/**
 * Determines if a user can edit a given meeting based on their permissions and the meeting's status.
 * @param meeting The meeting schedule data to evaluate.
 * @param user The user attempting to edit the meeting.
 * @returns An object containing whether the user can edit the meeting and the reason.
 */
export const canUserEditMeeting = (
  meeting: CalendarSlot | null | undefined,
  user: User | null | undefined
) => {

  if (!user) {
    return {
      canEdit: false,
      reason: "User does not exist"
    }
  }

  if (!meeting) {
    return {
      canEdit: false,
      reason: "Meeting does not exist"
    }
  }

  const { canJoin, status, message } = getMeetingStatus(meeting, true)

  if (canJoin) {
    return {
      canEdit: false,
      reason: "The meeting has started"
    }
  }

  if (status != MeetingStatus.Scheduled && status != MeetingStatus.Proposed) {
    return {
      canEdit: false,
      reason: message
    }
  }

  if (meeting.startTime < new Date()) {
    return {
      canEdit: false,
      reason: "The meeting has ended"
    }
  }

  if (!userHasPermission(UserType.User, user)) {
    return {
      canEdit: false,
      reason: "User does not have permission"
    }
  }

  if (userHasPermission(UserType.DeveloperAdmin, user)) {
    return {
      canEdit: true,
      reason: "User is developer admin"
    }
  }

  if (meeting.creatorId === user.userId) {
    return {
      canEdit: true,
      reason: "User is creator"
    }
  }

  if (meeting.attendeeIds.includes(user.userId)) {
    return {
      canEdit: true,
      reason: "User is an attendee"
    }
  }

  if (meeting.organisationIds) {
    for (let organisationId of user.organisationIds) {
      if (meeting.organisationIds.includes(organisationId)) {
        return {
          canEdit: true,
          reason: "User is an organisation member"
        }
      }
    }
  }

  return {
    canEdit: false,
    reason: "User does not have permission to edit meeting"
  }
}

/**
 * Retrieves the numeric mapping for a given user type.
 * @param userType The user type to map.
 * @returns The numeric value associated with the user type.
 */
export const getUserTypeMapping = (userType: UserType) => {
  switch (userType) {
    case UserType.Inactive:
      return 0
    case UserType.Guest:
      return 1
    case UserType.User:
      return 2
    case UserType.OrgAdmin:
      return 3
    case UserType.SysAdmin:
      return 4
    case UserType.DeveloperAdmin:
      return 5
    case UserType.Blocked:
      return -10000
    default:
      return -1
  }
}

/**
 * Checks if a user has the required permission level.
 * @param requiredLevel The required user type level.
 * @param user The user object containing the user's type.
 * @returns A boolean indicating whether the user has the required permission.
 */
export const userHasPermission = (
  requiredLevel: UserType,
  user: {
    userType: UserType,
  } | null | undefined
) => {

  if (!user) {
    return false;
  }

  if (user.userType == UserType.Blocked) {
    return false
  }

  const userTypeValue = getUserTypeMapping(user.userType)
  const requiredTypeValue = getUserTypeMapping(requiredLevel)

  return userTypeValue >= requiredTypeValue;
}

/**
 * Sorts two time slots based on their start and end times.
 * @param a The first time slot to compare.
 * @param b The second time slot to compare.
 * @returns A negative number if a comes before b, a positive number if a comes after b, or 0 if they are equal.
 */
export const sortTimeSlots = (a: Timeslot, b: Timeslot) => {
  const startDiff = a.startTime.getTime() - b.startTime.getTime()
  if (startDiff !== 0) return startDiff

  return a.endTime.getTime() - b.endTime.getTime()
}

/**
 * Combines overlapping or closely spaced user time slots into consolidated proposed time slots.
 * @param proposedTimes The array of proposed time slots to combine.
 * @param creatorId The ID of the user creating the combined time slots.
 * @param minDurationMin Optional minimum duration in minutes for each combined time slot.
 * @returns An array of consolidated proposed time slots.
 */
export const combineUserTimeSlots = (
  proposedTimes: Timeslot[],
  creatorId: string,
  minDurationMin?: number
): ProposedTimeSlot[] => {

  if (proposedTimes.length == 0) {
    return []
  }

  proposedTimes.sort(sortTimeSlots)

  const finalTimes: ProposedTimeSlot[] = []
  let currentSlot: ProposedTimeSlot | null = null
  for (let item of proposedTimes) {
    if (!currentSlot) {
      currentSlot = {
        ...item,
        creatorId,
        startTime: new Date(item.startTime),
        endTime: new Date(item.endTime)
      }
      continue
    }

    const timeDiff = item.startTime.getTime() - currentSlot.endTime.getTime()

    // Time difference is greater than five min
    if (timeDiff > 1000 * 60 * 5) {
      finalTimes.push(currentSlot)
      currentSlot = {
        ...item,
        creatorId,
        startTime: new Date(item.startTime),
        endTime: new Date(item.endTime)
      }
      continue
    }

    if (item.endTime > currentSlot.endTime) {
      currentSlot.endTime = new Date(item.endTime)
    }
  }

  if (currentSlot) {
    finalTimes.push(currentSlot)
  }

  if (minDurationMin && minDurationMin > 0) {
    return finalTimes.map(item => {
      const duration = (item.endTime.getTime() - item.startTime.getTime()) / (1000 * 60)

      if (duration >= minDurationMin) {
        return item
      }

      const endTime = new Date(item.startTime)
      endTime.setMinutes(endTime.getMinutes() + 30)

      return {
        ...item,
        endTime,
      }
    })
  }

  return finalTimes
}

/**
 * Splits time slots into smaller intervals based on the specified step and duration.
 * @param timeSlots The array of time slots to split.
 * @param stepMinutes The step interval in minutes for splitting the time slots.
 * @param durationMinutes The duration in minutes for each resulting time slot.
 * @returns An array of split time slots.
 */
export const splitTimeSlots = <T extends Timeslot>(
  timeSlots: T[],
  stepMinutes: number,
  durationMinutes: number,
): T[] => {
  if (stepMinutes < 1) {
    throw new Error("Invalid step minutes")
  }

  if (durationMinutes < 1) {
    throw new Error("Invalid duration minutes")
  }

  if (timeSlots.length == 0) {
    return []
  }

  const finalTimes: T[] = []
  for (let item of timeSlots) {
    const duration = Math.round((item.endTime.getTime() - item.startTime.getTime()) / (1000 * 60))
    if (duration <= durationMinutes) {
      finalTimes.push(item)
      continue
    }

    let startTime = new Date(item.startTime)
    for (let time = 0; time < duration; time += stepMinutes) {
      const endTime = new Date(startTime)
      endTime.setMinutes(endTime.getMinutes() + durationMinutes)

      if (endTime > item.endTime) {
        break
      }

      finalTimes.push({
        ...item,
        startTime: new Date(startTime),
        endTime: new Date(endTime)
      })

      startTime.setMinutes(startTime.getMinutes() + stepMinutes)
    }
  }

  return finalTimes
}

/**
 * Checks if two time slots overlap.
 * @param a The first time slot to check.
 * @param b The second time slot to check.
 * @returns A boolean indicating whether the time slots overlap.
 */
export const rangesOverlap = (a: Timeslot, b: Timeslot) => {
  return a.startTime < b.endTime && b.startTime < a.endTime
}

/**
 * Checks if two Date objects are equal within an optional tolerance.
 * @param a The first Date object to compare.
 * @param b The second Date object to compare.
 * @param tolerance Optional tolerance in milliseconds for the comparison.
 * @returns A boolean indicating whether the dates are equal within the specified tolerance.
 */
export const timesEqual = (a: Date, b: Date, tolerance?: number): boolean => {

  const diff = Math.abs(a.getTime() - b.getTime())

  if (tolerance != undefined && tolerance >= 0) {
    return diff < tolerance
  }

  return diff < 1000 * 10
}
/**
 * Checks if two time slots are equal within an optional tolerance.
 * @param a The first time slot to compare.
 * @param b The second time slot to compare.
 * @param tolerance Optional tolerance in milliseconds for the comparison.
 * @returns A boolean indicating whether the time slots are equal within the specified tolerance.
 */
export const timeSlotsEqual = (a: Timeslot, b: Timeslot, tolerance?: number) => {

  if (!timesEqual(a.startTime, b.startTime, tolerance)) {
    return false
  }

  if (!timesEqual(a.endTime, b.endTime, tolerance)) {
    return false
  }

  return true
}

/**
 * Determines the status of a patient's meeting based on the start time and current time.
 * @param data An object containing the start time and meeting type.
 * @returns The updated meeting status.
 */
export const checkPatientMeetingStatus = (data: {
  startTime: Date | undefined,
  meetingType: MeetingStatus
}): MeetingStatus => {
  if (!data.startTime) {
    return data.meetingType
  }

  // Meetings before the 7/10/2026 are before the function to set meeting status to Ended was implemented
  // It is more useful to consider these meetings as ended rather than abandoned
  const beforeFunctionExisted = new Date('2026-07-10T00:00:00Z')
  if (data.startTime.getTime() < beforeFunctionExisted.getTime()) {
    return MeetingStatus.Ended
  }

  if (data.meetingType != MeetingStatus.Scheduled && data.meetingType != MeetingStatus.Started) {
    return data.meetingType
  }

  const now = new Date()
  const expectedEndTime = new Date(data.startTime)
  expectedEndTime.setMinutes(expectedEndTime.getMinutes() + 30)

  if (expectedEndTime.getTime() < now.getTime()) {
    if (data.meetingType == MeetingStatus.Started) {
      return MeetingStatus.Ended
    }

    return MeetingStatus.Abandoned
  }

  return data.meetingType
}